import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { clipboardLink, clipboardReadable } from '../lib/clipboard.ts';
import { ImportSheet, type ImportPrefill } from '../features/import/ImportSheet.tsx';

interface ImportSheetApi {
  /** Opens the sheet; without a prefill it uses the clipboard if that needs no prompt. */
  open: (prefill?: ImportPrefill) => void;
}

const Context = createContext<ImportSheetApi>({ open: () => undefined });

export const useImportSheet = () => useContext(Context);

/** One Import sheet for the whole app, opened from the tab bar or `/import?url=`. */
export function ImportSheetProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; prefill: ImportPrefill }>({
    open: false,
    prefill: {},
  });
  const open = useCallback((prefill?: ImportPrefill) => {
    if (prefill) {
      setState({ open: true, prefill });
      return;
    }
    setState({ open: true, prefill: {} });
    void clipboardReadable()
      .then((readable) => (readable ? clipboardLink() : null))
      .then((url) => {
        if (url) setState((s) => (s.open ? { ...s, prefill: { url, fromClipboard: true } } : s));
      });
  }, []);
  const api = useMemo(() => ({ open }), [open]);
  return (
    <Context.Provider value={api}>
      {children}
      <ImportSheet
        open={state.open}
        prefill={state.prefill}
        onClose={() => setState((s) => ({ ...s, open: false }))}
      />
    </Context.Provider>
  );
}
